import {
  isMfaChallenge,
  type LoginResponse,
  type MfaChallengeResponse,
  mfaCodeSchema,
  mfaSignInCodeSchema,
  type SystemAdminLogin,
  systemAdminLoginSchema,
} from "@eduleo/shared";
import { useMutation } from "@tanstack/react-query";
import { Mail, ShieldCheck } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { Button } from "../../components/ui/button";
import { Alert } from "../../components/ui/feedback";
import { PasswordField } from "../../components/ui/password-field";
import { TextField } from "../../components/ui/text-field";
import { useCountdown } from "../../hooks/use-countdown";
import { ApiError, describeError } from "../../lib/api-client";
import { isPlatformPath, LOGIN_PATH, landingFor } from "../../lib/sign-in-paths";
import { useAuthStore } from "../../stores/auth-store";
import { resendSystemAdminCode, systemAdminLogin, verifySystemAdminCode } from "./auth-api";
import { CHANGE_PASSWORD_PATH } from "./require-auth";
import { SignInLayout } from "./sign-in-hero";

type FieldName = keyof SystemAdminLogin;
const FIELD_ORDER: FieldName[] = ["userId", "password"];
const CONSOLE_PATH = "/platform";

type Message = { title: string; detail?: string };

/** Only console pages are worth going back to; anything else starts at the console. */
function destination(state: unknown): string {
  const from = (state as { from?: string } | null)?.from;
  return isPlatformPath(from) && from ? from : CONSOLE_PATH;
}

/**
 * Sign-in for Eduleo's own team: User ID and password (no Institution ID), then, when two-step
 * verification is on, a code from their authenticator app or email. Schools never see this
 * page; the school sign-in at /login doesn't mention it.
 */
export function SystemAdminLoginPage() {
  const status = useAuthStore((state) => state.status);
  const signedInUser = useAuthStore((state) => state.user);
  const setSession = useAuthStore((state) => state.setSession);
  const navigate = useNavigate();
  const location = useLocation();
  const from = destination(location.state);

  const [userId, setUserId] = useState("");
  const [challenge, setChallenge] = useState<MfaChallengeResponse | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  if (status === "signed_in") return <Navigate to={landingFor(signedInUser, from)} replace />;

  function finish(session: LoginResponse) {
    setSession(session);
    // A password someone else set (new account, reset) must be replaced before anything else.
    if (session.user.mustChangePassword) {
      navigate(CHANGE_PASSWORD_PATH, {
        replace: true,
        state: { from: landingFor(session.user, from), forced: true },
      });
    } else {
      navigate(landingFor(session.user, from), { replace: true });
    }
  }

  return (
    <SignInLayout
      audience="team"
      footer={
        <div className="mt-6 flex flex-col items-center gap-2 text-center text-ink-2 text-sm">
          <p>Forgot your password, or lost your phone? Ask a super admin on your team.</p>
          <Link
            to={LOGIN_PATH}
            className="inline-flex min-h-11 items-center font-semibold text-brand-fg underline-offset-4 hover:underline md:min-h-0"
          >
            Work at a school? Use the school sign-in
          </Link>
        </div>
      }
    >
      {challenge ? (
        <CodeStep
          key={challenge.challenge}
          challenge={challenge}
          onSession={finish}
          onTimedOut={() => {
            setChallenge(null);
            setTimedOut(true);
          }}
          onStartAgain={() => {
            setChallenge(null);
            setTimedOut(false);
          }}
        />
      ) : (
        <PasswordStep
          userId={userId}
          onUserIdChange={setUserId}
          timedOut={timedOut}
          onChallenge={(next) => {
            setTimedOut(false);
            setChallenge(next);
          }}
          onSession={finish}
        />
      )}
    </SignInLayout>
  );
}

// ---------------------------------------------------------------------------------------------
// Step 1: User ID and password
// ---------------------------------------------------------------------------------------------

function PasswordStep({
  userId,
  onUserIdChange,
  timedOut,
  onChallenge,
  onSession,
}: {
  userId: string;
  onUserIdChange: (value: string) => void;
  /** The last code step ran out of time; say so and ask for the password again. */
  timedOut: boolean;
  onChallenge: (challenge: MfaChallengeResponse) => void;
  onSession: (session: LoginResponse) => void;
}) {
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldName, string>>>({});
  const inputs = useRef<Partial<Record<FieldName, HTMLInputElement | null>>>({});

  const mutation = useMutation({
    mutationFn: systemAdminLogin,
    onSuccess: (response) => {
      // The password has done its job; don't keep it around for the code step.
      setPassword("");
      if (isMfaChallenge(response)) onChallenge(response);
      else onSession(response);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "VALIDATION_FAILED")
        showFieldErrors(error.fieldErrors);
      if (error instanceof ApiError && error.code === "INVALID_CREDENTIALS") {
        setPassword("");
        inputs.current.password?.focus();
      }
    },
  });

  function showFieldErrors(errors: { path: string; message: string }[]) {
    const next: Partial<Record<FieldName, string>> = {};
    for (const error of errors) {
      const field = error.path as FieldName;
      if (FIELD_ORDER.includes(field) && !next[field]) next[field] = error.message;
    }
    setFieldErrors(next);
    const first = FIELD_ORDER.find((field) => next[field]);
    if (first) inputs.current[first]?.focus();
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mutation.isPending) return;
    const parsed = systemAdminLoginSchema.safeParse({ userId, password });
    if (!parsed.success) {
      showFieldErrors(
        parsed.error.issues.map((issue) => ({
          path: String(issue.path[0]),
          message: issue.message,
        })),
      );
      return;
    }
    setFieldErrors({});
    mutation.mutate(parsed.data);
  }

  const serverError =
    mutation.error &&
    !(mutation.error instanceof ApiError && mutation.error.code === "VALIDATION_FAILED")
      ? describeError(mutation.error)
      : null;

  return (
    <>
      <h2 className="font-bold text-2xl">Eduleo team sign-in</h2>
      <p className="mt-1 text-ink-2">For Eduleo staff. Sign in with your User ID and password.</p>

      {timedOut && !serverError && (
        <Alert tone="warning" title="Your sign-in timed out" className="mt-5">
          For your safety, enter your password again. We'll then ask for a new code.
        </Alert>
      )}
      {serverError && (
        <Alert tone="danger" title={serverError.title} className="mt-5">
          {serverError.detail}
        </Alert>
      )}

      <form
        noValidate
        onSubmit={onSubmit}
        className="mt-6 flex flex-col gap-5"
        aria-label="Eduleo team sign-in"
      >
        <TextField
          ref={(element) => {
            inputs.current.userId = element;
          }}
          label="User ID"
          name="userId"
          value={userId}
          onChange={(event) => {
            onUserIdChange(event.target.value);
            if (fieldErrors.userId)
              setFieldErrors((current) => ({ ...current, userId: undefined }));
          }}
          error={fieldErrors.userId}
          hint="Given to you by a super admin on your team."
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={!userId}
        />
        <PasswordField
          ref={(element) => {
            inputs.current.password = element;
          }}
          label="Password"
          name="password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            if (fieldErrors.password)
              setFieldErrors((current) => ({ ...current, password: undefined }));
          }}
          error={fieldErrors.password}
          autoComplete="current-password"
          autoFocus={Boolean(userId)}
        />
        <Button
          type="submit"
          size="lg"
          block
          loading={mutation.isPending}
          loadingText="Signing in…"
        >
          Sign in
        </Button>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Step 2: the code from the authenticator app or email
// ---------------------------------------------------------------------------------------------

/** Counts whole seconds down to zero, once a second. */
const linkButton =
  "inline-flex min-h-11 cursor-pointer items-center font-semibold text-brand-fg text-sm underline-offset-4 hover:underline md:min-h-0";

function CodeStep({
  challenge,
  onSession,
  onTimedOut,
  onStartAgain,
}: {
  challenge: MfaChallengeResponse;
  onSession: (session: LoginResponse) => void;
  onTimedOut: () => void;
  onStartAgain: () => void;
}) {
  const byEmail = challenge.method === "email";
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [problem, setProblem] = useState<Message | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState(challenge.sentTo);
  const [waitSeconds, startWait] = useCountdown();
  const input = useRef<HTMLInputElement>(null);

  function failed(failure: Error, onField?: (message: string) => void) {
    if (failure instanceof ApiError && failure.code === "MFA_CHALLENGE_EXPIRED") {
      onTimedOut();
      return;
    }
    const field =
      failure instanceof ApiError ? failure.fieldErrors.find((item) => item.path === "code") : null;
    if (field && onField) {
      onField(field.message);
      return;
    }
    setNotice(null);
    setProblem(describeError(failure));
  }

  const verify = useMutation({
    mutationFn: verifySystemAdminCode,
    onSuccess: onSession,
    onError: (failure) =>
      failed(failure, (message) => {
        // A wrong code is cleared so the next one can be typed straight in.
        setError(message);
        setCode("");
        input.current?.focus();
      }),
  });

  const resend = useMutation({
    mutationFn: () => resendSystemAdminCode(challenge.challenge),
    onSuccess: (sent) => {
      setSentTo(sent.sentTo);
      setProblem(null);
      setError(undefined);
      setCode("");
      setNotice(`We sent a new code to ${sent.sentTo}. Use the newest code.`);
      input.current?.focus();
    },
    onError: (failure) => {
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        startWait(failure.retryAfterSeconds);
      }
      failed(failure);
    },
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (verify.isPending) return;
    const parsed = (recovery ? mfaSignInCodeSchema : mfaCodeSchema).safeParse(code);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message);
      input.current?.focus();
      return;
    }
    setError(undefined);
    setProblem(null);
    verify.mutate({ challenge: challenge.challenge, code: parsed.data });
  }

  function switchMode() {
    setRecovery((value) => !value);
    setCode("");
    setError(undefined);
    input.current?.focus();
  }

  const title = byEmail
    ? "Check your email"
    : recovery
      ? "Enter a recovery code"
      : "Enter the code from your app";
  const description = byEmail ? (
    <>
      We sent a 6-digit code to <strong className="text-ink">{sentTo}</strong>. It can take a minute
      to arrive. Check your spam folder too.
    </>
  ) : recovery ? (
    "Use one of the recovery codes you saved when you set up the app. Each code works once."
  ) : (
    "Open the authenticator app on your phone and type the 6-digit code it shows for Eduleo."
  );
  const Icon = byEmail ? Mail : ShieldCheck;

  return (
    <>
      <span className="grid size-12 place-items-center rounded-[14px] bg-brand-soft text-brand-fg">
        <Icon aria-hidden className="size-6" strokeWidth={1.5} />
      </span>
      <h2 className="mt-4 font-bold text-2xl">{title}</h2>
      <p className="mt-1 text-ink-2">{description}</p>

      {problem && (
        <Alert tone="danger" title={problem.title} className="mt-5">
          {problem.detail}
        </Alert>
      )}
      {notice && !problem && <Alert tone="success" title={notice} className="mt-5" />}

      <form
        noValidate
        onSubmit={onSubmit}
        className="mt-6 flex flex-col gap-5"
        aria-label="Two-step verification"
      >
        {recovery ? (
          <TextField
            key="recovery"
            ref={input}
            label="Recovery code"
            name="recoveryCode"
            hint="Looks like abcde-12345."
            value={code}
            error={error}
            onChange={(event) => {
              setCode(event.target.value);
              if (error) setError(undefined);
            }}
            className="font-mono"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="done"
            autoFocus
          />
        ) : (
          <TextField
            key="code"
            ref={input}
            label="6-digit code"
            name="code"
            value={code}
            error={error}
            onChange={(event) => {
              setCode(event.target.value);
              if (error) setError(undefined);
            }}
            className="font-mono text-lg tracking-[0.2em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            enterKeyHint="done"
            autoFocus
          />
        )}
        <Button
          type="submit"
          size="lg"
          block
          loading={verify.isPending}
          loadingText="Checking the code…"
        >
          Sign in
        </Button>
      </form>

      <div className="mt-4 flex flex-col items-start gap-1">
        {!byEmail && (
          <button type="button" className={linkButton} onClick={switchMode}>
            {recovery ? "Use the code from my app instead" : "Use a recovery code instead"}
          </button>
        )}
        {byEmail && (
          <>
            <Button
              variant="secondary"
              size="sm"
              loading={resend.isPending}
              loadingText="Sending…"
              disabled={waitSeconds > 0}
              onClick={() => {
                if (!resend.isPending) resend.mutate();
              }}
            >
              Send a new code
            </Button>
            {waitSeconds > 0 && (
              <p className="text-ink-3 text-xs">
                You can ask for a new code in {waitSeconds} seconds.
              </p>
            )}
          </>
        )}
        <button type="button" className={linkButton} onClick={onStartAgain}>
          Start again with a different account
        </button>
      </div>
    </>
  );
}
